import { Dispatch } from 'react'
import * as Sentry from '@sentry/react'

import { forgotPasswordSubmit, initiateForgotPasswordFlow } from '@vacationtracker/shared/services/auth'

import { fetchAuthInfo } from '../auth-info'
import { AUTH_FLOW } from '../../../../services/analytics/auth-events'
import { trackSignin } from '../analytics'
import { authInfoToOutcome, resetErrorToReason } from '../outcomes'
import { NewSigninAction, SigninStep } from '../types'

export interface IUsePasswordResetParams {
  step: SigninStep
  localDispatch: Dispatch<NewSigninAction>
}

/** What the code was sent to, carried to the step that asks for it. */
interface ISentCodeTarget {
  email: string
  username: string
  adpEnabled: boolean
}

export interface ISubmitNewPasswordParams {
  code: string
  password: string
}

export interface IPasswordReset {
  requestCode: (email: string) => Promise<void>
  resendCode: () => Promise<void>
  submitNewPassword: (params: ISubmitNewPasswordParams) => Promise<void>
}

const report = (error, reason: string) => {
  if (reason === 'unknown') {
    Sentry.captureException(error, { tags: { flow: AUTH_FLOW, reason: 'password_reset' } })
  }
}

export const usePasswordReset = ({ step, localDispatch }: IUsePasswordResetParams): IPasswordReset => {
  // No password account behind the address: it reaches the code step as an unknown one does, so
  // the answer cannot tell an address with only a platform account from one with no account.
  const sendCode = async (params: ISentCodeTarget) => {
    if (params.username) {
      await initiateForgotPasswordFlow(params.username)
    }
    localDispatch({ type: 'resetCodeSent', ...params })
  }

  const failReset = (error) => {
    const reason = resetErrorToReason(error)
    report(error, reason)
    localDispatch({ type: 'resetFailed', reason })
  }

  const requestCode = async (email: string) => {
    localDispatch({ type: 'resetSubmitted' })
    trackSignin('SIGN_IN_PASSWORD_RESET_REQUESTED')
    try {
      const outcome = authInfoToOutcome(await fetchAuthInfo(email))
      // An address with accounts on several platforms carries the username when one of them is
      // an email account, and that account's password is the one to reset.
      const account = outcome.kind === 'emailPlatform' || outcome.kind === 'severalPlatforms' ? outcome : undefined
      await sendCode({
        email,
        username: account?.username ?? '',
        adpEnabled: account?.adpEnabled ?? false,
      })
    } catch (error) {
      // An address with no account still reaches the code step. Saying so here would answer
      // "does this address have an account?" to anyone who asks, which is the whole point of
      // the flow being quiet about it (VT-6076).
      if (error?.response?.data?.code === 'UserNotFound') {
        localDispatch({ type: 'resetCodeSent', email, username: '', adpEnabled: false })
        return
      }
      if (error?.response?.data?.code === 'EmailNotValid') {
        localDispatch({ type: 'bannerRaised', banner: { kind: 'invalidEmail' } })
        return
      }
      failReset(error)
    }
  }

  const resendCode = async () => {
    if (step.name !== 'resetCode') return
    localDispatch({ type: 'resetSubmitted' })
    try {
      // Mirrors the first request: with no account to send to, the resend answers as though it went.
      if (step.username) {
        await initiateForgotPasswordFlow(step.username)
      }
      localDispatch({ type: 'resetCodeResent' })
    } catch (error) {
      failReset(error)
    }
  }

  const submitNewPassword = async ({ code, password }: ISubmitNewPasswordParams) => {
    if (step.name !== 'resetCode') return

    // The address reached this step without resolving to an account, so there is no code that
    // can work. Answering as though the code were wrong keeps the flow from confirming it.
    if (!step.username) {
      localDispatch({ type: 'resetFailed', reason: 'codeMismatch' })
      return
    }

    localDispatch({ type: 'resetSubmitted' })
    try {
      await forgotPasswordSubmit(step.username, code.trim(), password)
      trackSignin('SIGN_IN_PASSWORD_RESET_COMPLETED')
      localDispatch({ type: 'passwordResetCompleted' })
    } catch (error) {
      failReset(error)
    }
  }

  return { requestCode, resendCode, submitNewPassword }
}
